import { c as _c } from "react/compiler-runtime";
import { useField, useFormikContext } from 'formik';
import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import Chip from '../../base/Chip';
import Text from '../../base/Text';
import { Div } from '../../helpers/StyledUtils';
interface Props {
  label?: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  help?: string;
  mono?: boolean;
  allow_duplicates?: boolean;
}

// Commas, whitespace (including new lines), pipes and semicolons all separate tokens
const SEPARATOR_REGEX = /[\s,|;]+/;
const splitTokens = (value: string) => value.split(SEPARATOR_REGEX).filter(Boolean);
const StyledFieldContainer = styled(Div)<{
  focused: boolean;
}>(({
  theme,
  focused
}) => css`
    cursor: text;
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1.5)};
    background-color: ${theme.colors.surface.base.surface};
    border-radius: ${theme.radius.normal};
    box-shadow: ${focused ? theme.shadows.input.focused : theme.shadows.neutral.base};
  `);
const StyledInput = styled.input(({
  theme
}) => css`
    font-size: ${theme.pxToRem(theme.font_sizes.s.font_size)};
    line-height: ${theme.pxToRem(theme.font_sizes.s.line_height)};
    font-family:
      JetBrains Mono,
      monospace;
    flex: 1;
    min-width: 120px;
    border: none;
    outline: none;
    background: transparent;
    padding: 0;

    &:focus {
      outline: none;
    }
  `);
const StyledFocusArea = styled(Div)`
  position: absolute;
  cursor: text;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;
const validate = (value: string[] | undefined, required: boolean) => {
  if ((!value || value.length < 1) && required) return 'Required';
};
const MultiTokenInput: React.FC<Props> = t0 => {
  const $ = _c(57);
  const {
    label,
    name,
    placeholder: t1,
    required: t2,
    help,
    mono: t3,
    allow_duplicates: t4
  } = t0;
  const placeholder = t1 === undefined ? "Type and press space or enter..." : t1;
  const required = t2 === undefined ? false : t2;
  const mono = t3 === undefined ? true : t3;
  const allow_duplicates = t4 === undefined ? false : t4;
  const theme = useTheme();
  let t5;
  if ($[0] !== required) {
    t5 = value => validate(value, required);
    $[0] = required;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== name || $[3] !== t5) {
    t6 = {
      name,
      validate: t5
    };
    $[2] = name;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const [field, t7, helpers] = useField(t6);
  const {
    error,
    touched
  } = t7;
  const {
    submitCount
  } = useFormikContext();
  const [input_value, setInputValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [last_token_selected, setLastTokenSelected] = useState(false);
  const input_ref = useRef(null);
  const container_ref = useRef(null);
  const tokens = field.value || [];
  const visible_tokens = focused ? tokens : tokens.slice(0, 5);
  const hidden_count = focused ? 0 : Math.max(0, tokens.length - 5);
  const addTokens = candidates => {
    const next_tokens = [...tokens];
    candidates.forEach(candidate => {
      const trimmed = candidate.trim();
      if (trimmed && (allow_duplicates || !next_tokens.includes(trimmed))) {
        next_tokens.push(trimmed);
      }
    });
    if (next_tokens.length > tokens.length) {
      helpers.setTouched(true);
      helpers.setValue(next_tokens);
    }
  };
  const removeToken = index => {
    helpers.setTouched(true);
    helpers.setValue(tokens.filter((_, token_index) => token_index !== index));
    setLastTokenSelected(false);
    setTimeout(() => input_ref.current?.focus(), 0);
  };
  let t8;
  if ($[5] !== addTokens) {
    t8 = e => {
      const value_0 = e.target.value;
      setLastTokenSelected(false);
      if (SEPARATOR_REGEX.test(value_0)) {
        const parts = value_0.split(SEPARATOR_REGEX);
        const remainder = parts.pop() || "";
        addTokens(parts);
        setInputValue(remainder);
      } else {
        setInputValue(value_0);
      }
    };
    $[5] = addTokens;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const handleChange = t8;
  let t9;
  if ($[7] !== addTokens || $[8] !== input_value) {
    t9 = e_0 => {
      const pasted = e_0.clipboardData.getData("text");
      if (!SEPARATOR_REGEX.test(pasted.trim())) {
        return;
      }
      e_0.preventDefault();
      addTokens([...splitTokens(input_value), ...splitTokens(pasted)]);
      setInputValue("");
      setLastTokenSelected(false);
    };
    $[7] = addTokens;
    $[8] = input_value;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const handlePaste = t9;
  const handleKeyDown = e_1 => {
    if (e_1.key === "Enter") {
      e_1.preventDefault();
      if (input_value.trim()) {
        addTokens([input_value]);
        setInputValue("");
      }
    } else {
      if (e_1.key === "Backspace" && !input_value && tokens.length > 0) {
        e_1.preventDefault();
        if (last_token_selected) {
          removeToken(tokens.length - 1);
          setLastTokenSelected(tokens.length > 1);
        } else {
          setLastTokenSelected(true);
        }
      } else {
        if (last_token_selected) {
          setLastTokenSelected(false);
        }
      }
    }
  };
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setFocused(true);
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const handleFocus = t10;
  let t11;
  if ($[11] !== addTokens || $[12] !== input_value) {
    t11 = e_2 => {
      if (container_ref.current?.contains(e_2.relatedTarget as Node | null)) {
        return;
      }
      setFocused(false);
      setLastTokenSelected(false);
      if (input_value.trim()) {
        addTokens([input_value]);
        setInputValue("");
      }
    };
    $[11] = addTokens;
    $[12] = input_value;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const handleBlur = t11;
  const handleContainerMouseDown = e_3 => {
    if (focused && e_3.target !== input_ref.current) {
      e_3.preventDefault();
    }
  };
  const handleContainerClick = () => {
    setFocused(true);
    setTimeout(() => input_ref.current?.focus(), 0);
  };
  let t12;
  if ($[14] !== focused) {
    t12 = [focused];
    $[14] = focused;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  useEffect(() => {
    if (focused && input_ref.current) {
      input_ref.current.focus();
    }
  }, t12);
  const T0 = Div;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      b: 4
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== label || $[18] !== name || $[19] !== required) {
    t14 = label && <Text as="label" htmlFor={name} size="xs" semi={true} muted={true} m={{
      b: 1
    }}>{label}{required && <Text as="span" danger={true}>*</Text>}</Text>;
    $[17] = label;
    $[18] = name;
    $[19] = required;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const T1 = StyledFieldContainer;
  const t15 = visible_tokens.map((token, index_0) => <Chip key={`${token}-${index_0}`} mono={mono} style={{
    zIndex: focused ? 2 : undefined,
    ...(last_token_selected && index_0 === tokens.length - 1 && {
      outline: `2px solid ${theme.colors.outline.focus.primary}`,
      outlineOffset: "1px"
    })
  }} label={token} icon="close" onClick={e_4 => {
    e_4?.stopPropagation();
    removeToken(index_0);
  }} />);
  let t16;
  if ($[21] !== hidden_count) {
    t16 = hidden_count > 0 && <Text size="s" muted={true}>+ {hidden_count} more</Text>;
    $[21] = hidden_count;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== focused || $[24] !== tokens.length) {
    t17 = !focused && tokens.length > 0 && <StyledFocusArea />;
    $[23] = focused;
    $[24] = tokens.length;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const t18 = tokens.length === 0 ? placeholder : "";
  let t19;
  if ($[26] !== handleBlur || $[27] !== handleChange || $[28] !== handleFocus || $[29] !== handleKeyDown || $[30] !== handlePaste || $[31] !== input_ref || $[32] !== input_value || $[33] !== t18) {
    t19 = <StyledInput ref={input_ref} value={input_value} onChange={handleChange} onPaste={handlePaste} onKeyDown={handleKeyDown} onFocus={handleFocus} onBlur={handleBlur} placeholder={t18} />;
    $[26] = handleBlur;
    $[27] = handleChange;
    $[28] = handleFocus;
    $[29] = handleKeyDown;
    $[30] = handlePaste;
    $[31] = input_ref;
    $[32] = input_value;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== T1 || $[36] !== focused || $[37] !== handleContainerClick || $[38] !== handleContainerMouseDown || $[39] !== t15 || $[40] !== t16 || $[41] !== t17 || $[42] !== t19) {
    t20 = <T1 ref={container_ref} focused={focused} onMouseDown={handleContainerMouseDown} onClick={handleContainerClick}>{t15}{t16}{t17}{t19}</T1>;
    $[35] = T1;
    $[36] = focused;
    $[37] = handleContainerClick;
    $[38] = handleContainerMouseDown;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== help) {
    t21 = help && <Text m={{
      t: 1
    }} size="xs" muted={true}>{help}</Text>;
    $[44] = help;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== error || $[47] !== submitCount || $[48] !== touched) {
    t22 = (touched || submitCount > 0) && error && <Text m={{
      t: 1,
      b: 0
    }} as="p" danger={true} size="s">{error}</Text>;
    $[46] = error;
    $[47] = submitCount;
    $[48] = touched;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== T0 || $[51] !== t13 || $[52] !== t14 || $[53] !== t20 || $[54] !== t21 || $[55] !== t22) {
    t23 = <T0 m={t13}>{t14}{t20}{t21}{t22}</T0>;
    $[50] = T0;
    $[51] = t13;
    $[52] = t14;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  return t23;
};
export default MultiTokenInput;